import React, { useEffect } from "react";
import GenieText from "../../../components/core/genie-text/GenieText";
import GeniePage from "../../../components/special/genie-page/GeniePage";
import {
  Divider,
  Stack,
  TextField,
  Box,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  FormControl,
} from "@mui/material";
import useOrganization from "../../../queries/routes/useOrganization";
import { useParams } from "react-router-dom";
import useUpdateOrganization from "../../../queries/routes/useUpdateOrganization";
import { Nullable } from "../../../utils/Nullable";
import GenieCard from "../../../components/special/genie-card/GenieCard";
import GenieStickyHeader from "../../../components/core/genie-sticky-header/GenieStickyHeader";
import NumberFormat from "react-number-format";

function Terms() {
  const [newBuyerQuantity, setNewBuyerQuantity] =
    React.useState<Nullable<number>>(0);
  const [returningBuyerQuantity, setReturningBuyerQuantity] =
    React.useState<Nullable<number>>(0);

  const [existingBuyerMinimum, setExistingBuyerMinimum] =
    React.useState<Nullable<string>>("0");
  const [newBuyerMinimum, setNewBuyerMinimum] =
    React.useState<Nullable<string>>("0");

  const { org_id } = useParams();
  const { data: org } = useOrganization(org_id!);
  const { mutate: updateOrg } = useUpdateOrganization(org_id!);

  useEffect(() => {
    setNewBuyerQuantity(org?.order_terms?.new_customer_min || 0);
    setReturningBuyerQuantity(org?.order_terms?.returning_customer_min || 0);

    (
      Math.round(Number(org?.order_terms?.new_customer_min_cents)) / 100
    ).toString();

    setExistingBuyerMinimum(
      (org?.order_terms?.returning_customer_min_cents &&
        (
          Math.round(Number(org?.order_terms?.returning_customer_min_cents)) /
          100
        ).toString()) ||
        "0"
    );
    setNewBuyerMinimum(
      (org?.order_terms?.new_customer_min_cents &&
        (
          Math.round(Number(org?.order_terms?.new_customer_min_cents)) / 100
        ).toString()) ||
        "0"
    );
  }, [org]);

  const handleNewValueChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setNewBuyerQuantity(newValue);
    updateOrg({
      ...org!,
      order_terms: {
        ...org!.order_terms,
        new_customer_min: newValue,
        returning_customer_min: returningBuyerQuantity || 0,
        returning_customer_min_cents: Math.round(
          Number(existingBuyerMinimum) * 100
        ),
        new_customer_min_cents: Math.round(Number(newBuyerMinimum) * 100),
      },
    });
  };

  const handleReturningValueChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setReturningBuyerQuantity(newValue);
    updateOrg({
      ...org!,
      order_terms: {
        ...org!.order_terms,
        returning_customer_min: newValue,
        new_customer_min: newBuyerQuantity || 0,
        returning_customer_min_cents: Math.round(
          Number(existingBuyerMinimum) * 100
        ),
        new_customer_min_cents: Math.round(Number(newBuyerMinimum) * 100),
      },
    });
  };

  // returning_customer_min_cents
  const handleReturningCentsValueChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setExistingBuyerMinimum(newValue.toString());
    updateOrg({
      ...org!,
      order_terms: {
        ...org!.order_terms,
        returning_customer_min: returningBuyerQuantity || 0,
        new_customer_min: newBuyerQuantity || 0,
        returning_customer_min_cents: Math.round(Number(newValue) * 100),
        new_customer_min_cents: Math.round(Number(newBuyerMinimum) * 100),
      },
    });
  };

  const handleNewCentsValueChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setNewBuyerMinimum(newValue.toString());
    updateOrg({
      ...org!,
      order_terms: {
        ...org!.order_terms,
        returning_customer_min: returningBuyerQuantity || 0,
        new_customer_min: newBuyerQuantity || 0,
        returning_customer_min_cents: Math.round(
          Number(existingBuyerMinimum) * 100
        ),
        new_customer_min_cents: Math.round(Number(newValue) * 100),
      },
    });
  };

  return (
    <GeniePage noMargin>
      <GenieStickyHeader>
        <Stack flexDirection={"row"} width={"100%"} alignItems={"center"}>
          <GenieText fontSize={"medium"} fontWeight={"bold"}>
            Terms
          </GenieText>
        </Stack>
      </GenieStickyHeader>
      <GenieCard>
        <GenieText paragraph>Order Minimums</GenieText>
        <GenieText paragraph fontSize={"small"}>
          Existing Customer
        </GenieText>
        <Box display={"flex"} alignItems={"end"}>
          <Box>
            <GenieText fontSize={"tiny"}>Basket Size Minimum</GenieText>
            <TextField
              sx={{ mt: 1 }}
              type={"number"}
              id="order-quantity-new"
              variant="outlined"
              onChange={(event) => {
                handleReturningValueChange(event, Number(event.target.value));
              }}
              value={returningBuyerQuantity}
            />
          </Box>
          <Box ml={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="existingMinimum">
                Minimum Cart Dollar Value
              </InputLabel>
              <NumberFormat
                allowNegative={false}
                decimalScale={2}
                decimalSeparator={"."}
                customInput={OutlinedInput}
                id={"existingMinimum"}
                required
                name={"existingMinimum"}
                onChange={(event: any) =>
                  handleReturningCentsValueChange(event, event?.target.value)
                }
                label="Minimum Cart Dollar Value"
                fullWidth
                value={existingBuyerMinimum}
                startAdornment={
                  <InputAdornment position="start">$</InputAdornment>
                }
              />
            </FormControl>
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <GenieText paragraph fontSize={"small"}>
          New Customer
        </GenieText>
        <Box display={"flex"} alignItems={"end"}>
          <Box>
            <GenieText fontSize={"tiny"}>Basket Size Minimum</GenieText>
            <TextField
              sx={{ mt: 1 }}
              type={"number"}
              id="order-quantity-new"
              variant="outlined"
              onChange={(event) => {
                handleNewValueChange(event, Number(event.target.value));
              }}
              value={newBuyerQuantity}
            />
          </Box>
          <Box ml={4}>
            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="newMinimum">
                Minimum Cart Dollar Value
              </InputLabel>
              <NumberFormat
                allowNegative={false}
                decimalScale={2}
                decimalSeparator={"."}
                customInput={OutlinedInput}
                id={"newMinimum"}
                required
                name={"newMinimum"}
                onChange={(event: any) =>
                  handleNewCentsValueChange(event, event?.target.value)
                }
                label="Minimum Cart Dollar Value"
                fullWidth
                value={newBuyerMinimum}
                startAdornment={
                  <InputAdornment position="start">$</InputAdornment>
                }
              />
            </FormControl>
          </Box>
        </Box>
      </GenieCard>
    </GeniePage>
  );
}

export default Terms;
